import { useEffect, useState } from 'react';
import { Alert, App, Button, Card, Checkbox, Col, Row, Space, Tag, Typography } from 'antd';
import { DEFAULT_SEND_STATUSES, SENDABLE_STATUSES, type EmailStatus } from '@crm/shared';
import { CardTitle } from '../../components/StatCard';
import { errorMessage } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { useSettings, useUpdateSettings } from '../../features/settings/api';
import { STATUS_LABEL } from '../../features/verification/api';

const RISKY = new Set<EmailStatus>(['INVALID', 'DISPOSABLE']);
const sameSet = (a: string[], b: string[]) => a.length === b.length && a.every((s) => b.includes(s));

/** Workspace switch for which email statuses campaigns send to. Suppressed is never sent to. */
export function CampaignSendSettings({ counts }: { counts: Partial<Record<EmailStatus, number>> }) {
  const { message } = App.useApp();
  const { user } = useAuth();
  const settings = useSettings();
  const update = useUpdateSettings();
  const saved = settings.data?.campaignSendStatuses ?? DEFAULT_SEND_STATUSES;
  const [picked, setPicked] = useState<EmailStatus[]>(saved);
  useEffect(() => setPicked(saved), [settings.data?.campaignSendStatuses]); // eslint-disable-line react-hooks/exhaustive-deps

  const canEdit = user?.role === 'SUPER_ADMIN';
  const dirty = !sameSet(picked, saved);
  const reach = picked.reduce((n, s) => n + (counts[s] ?? 0), 0);
  const save = () =>
    update
      .mutateAsync({ campaignSendStatuses: picked })
      .then(() => message.success('Campaign sending updated'))
      .catch((e) => message.error(errorMessage(e)));

  return (
    <Card
      style={{ marginBottom: 24 }}
      title={<CardTitle title="Campaign sending" subtitle="Which email statuses campaigns send to. Applies to every campaign; a campaign's “Only send to verified emails” switch narrows it to Valid (plus Unknown and Catch-all when the campaign opts in)." />}
      extra={
        canEdit && (
          <Space>
            {dirty && <Button onClick={() => setPicked(saved)}>Reset</Button>}
            <Button type="primary" disabled={!dirty || !picked.length} loading={update.isPending} onClick={save}>
              Save
            </Button>
          </Space>
        )
      }
      loading={settings.isLoading}
    >
      <Checkbox.Group value={picked} onChange={(v) => setPicked(v as EmailStatus[])} disabled={!canEdit} style={{ width: '100%' }}>
        <Row gutter={[16, 12]}>
          {SENDABLE_STATUSES.map((s) => (
            <Col xs={24} sm={12} lg={8} key={s}>
              <Checkbox value={s}>
                <b>{STATUS_LABEL[s]}</b> <Typography.Text type="secondary">({(counts[s] ?? 0).toLocaleString()})</Typography.Text> {picked.includes(s) ? <Tag color="green">Sent</Tag> : <Tag>Skipped</Tag>}
              </Checkbox>
            </Col>
          ))}
          <Col xs={24} sm={12} lg={8}>
            <Checkbox checked={false} disabled>
              <b>{STATUS_LABEL.SUPPRESSED}</b> <Typography.Text type="secondary">({(counts.SUPPRESSED ?? 0).toLocaleString()})</Typography.Text> <Tag>Never sent</Tag>
            </Checkbox>
          </Col>
        </Row>
      </Checkbox.Group>
      <Typography.Paragraph type="secondary" style={{ margin: '16px 0 0' }}>
        Up to <b>{reach.toLocaleString()}</b> contacts can receive campaigns with this choice (before unsubscribes and duplicate addresses are left out).
      </Typography.Paragraph>
      {!picked.length && <Alert type="error" showIcon style={{ marginTop: 12 }} title="Pick at least one status, or no campaign can send." />}
      {picked.some((s) => RISKY.has(s)) && (
        <Alert type="warning" showIcon style={{ marginTop: 12 }} title="Invalid and disposable addresses bounce or go nowhere" description="Sending to them raises your bounce rate and can hurt delivery for every campaign. Bounces are added to the suppression list automatically." />
      )}
      {!canEdit && <Typography.Text type="secondary">Only a super admin can change this.</Typography.Text>}
    </Card>
  );
}
